// Signing roles: the seats a document names, per company and per side.
//
// CONSUMER: shared (Workers send route + web prep screen + node-pool tests).
//
// Nate, 2026-09-08: "There are no roles. There is no way for you to
// designate what roles fill out or sign what blanks or fields in the
// form." A field on a form belongs to a ROLE ("Buyer 1", "Borrower",
// "Grantor"), never directly to a person; a SEND casts a person into
// each role from the record, and every field follows its role. This
// file is the catalog: what the roles are, which company and side offer
// them, what kind each is (signs / name only / gets a copy), and the
// plain-English rule the OS runs to fill it. The casting itself lives
// in role-casting.ts.
//
// Every company is here, not only Realty (Grant, 2026-09-15: the Realty
// designs must upgrade the whole e-sign platform). Presets are keyed per
// company so a Realty seat list never appears on an Insurance send
// ("default to your desk"). The SHAPE is shared: the record's first
// person, its second person, the record's owner (stamped, never
// emailed), an optional second advisor, the counter-party (name only),
// and the vendors (name only). Each company supplies its own words.
//
// Pure and dependency-free, the field-types.ts rule, so the web bundle
// and the Workers runtime read one catalog and tests need no database.

/** What a role does on the document.
 *  - `signs`: a real signer, emailed a link.
 *  - `name_only`: printed (a name, an address block) and never asked to
 *    sign — the seller on a buy-side copy, the escrow officer.
 *  - `stamp`: the record's own advisor; their saved signature is stamped
 *    before send (Grant, 2026-08-22: never email ourselves an envelope). */
export type RoleKind = "signs" | "name_only" | "stamp";

/** How the OS proposes a person for a role. Stored as a short string on
 *  template seats and layout fields; parsed with parseFillRule. */
export type FillRule =
  | { kind: "record_person"; index: number }
  | { kind: "record_owner_user" }
  | { kind: "party"; partyRole: string }
  | { kind: "contract_card"; field: "other_party_name" }
  // The firm's standing holder of a seat (the managing broker), read from
  // firm_signing_roles. Offered, never forced onto a send.
  | { kind: "firm_role"; roleKey: string }
  | { kind: "manual" };

/** The tone a role's fields are drawn in. Colour follows the ROLE, not
 *  the person (swap Bob and Sue and the colours stay with Buyer 1 and
 *  Buyer 2), and the tones map onto the prep screen's existing palette:
 *  first person blue, second person amber, the advisor green, the
 *  broker navy, everyone name-only a dashed neutral. */
export type RoleTone = "first" | "second" | "third" | "fourth" | "owner" | "broker" | "neutral";

export interface SigningRole {
  /** Stable key stored on signer rows, layout fields and template seats
   *  (`buyer_1`, `co_borrower`). Never shown; the label is. */
  key: string;
  label: string;
  kind: RoleKind;
  fillRule: FillRule;
  tone: RoleTone;
  /** An optional seat is one a real send often leaves empty (a second
   *  buyer on a single-buyer household, a guarantor). "Nobody" on an
   *  optional seat is a normal outcome shown with its field count, not
   *  a blocker. A required seat with nobody in it blocks the send. */
  optional?: boolean;
  /** Words a form's own caption uses for this seat ("buyer", "co-
   *  borrower"), lower-case. matchCaptionToRole reads these so a field
   *  Stew proposes lands under the right role. Longer phrases are
   *  tried first, so "second buyer" beats "buyer". */
  captionWords: readonly string[];
}

/** Which side of a two-sided company's record a send is for. Only
 *  Realty has sides today; every other company is `na`. */
export type RoleSide = "buy" | "sell" | "na";

const person = (index: number): FillRule => ({ kind: "record_person", index });
const owner: FillRule = { kind: "record_owner_user" };
const party = (partyRole: string): FillRule => ({ kind: "party", partyRole });
const contractCard: FillRule = { kind: "contract_card", field: "other_party_name" };
const manual: FillRule = { kind: "manual" };
const firmRole = (roleKey: string): FillRule => ({ kind: "firm_role", roleKey });

const REALTY_BUY: readonly SigningRole[] = [
  { key: "buyer_1", label: "Buyer 1", kind: "signs", fillRule: person(0), tone: "first", captionWords: ["buyer 1", "first buyer", "buyer"] },
  { key: "buyer_2", label: "Buyer 2", kind: "signs", fillRule: person(1), tone: "second", optional: true, captionWords: ["buyer 2", "second buyer", "co-buyer"] },
  // Buyers 3 and 4 (Nate + Grant, 2026-09-23): a family buying across two
  // households, adult kids and their parents, can be four buyers. The
  // contract prints two buyer lines; the third and fourth sign on an
  // addendum placed on the same send. Any of the four people on the record
  // can be put in any of these seats with "Change". Optional, and the send
  // screen only shows them when the record actually has a third or fourth
  // person (seatAppliesToRecord), so a two-buyer send looks as before.
  { key: "buyer_3", label: "Buyer 3", kind: "signs", fillRule: person(2), tone: "third", optional: true, captionWords: ["buyer 3", "third buyer"] },
  { key: "buyer_4", label: "Buyer 4", kind: "signs", fillRule: person(3), tone: "fourth", optional: true, captionWords: ["buyer 4", "fourth buyer"] },
  // Two agent signatures side by side on one line are Agent 1 and Agent
  // 2 on the client's file (Nate, 2026-09-14); the second seat is empty
  // when one agent works the file. Agent 1 is stamped, never emailed —
  // it is whoever is sending, and "we never email ourselves an
  // envelope" (Grant, 2026-08-22). Agent 2 is a colleague, not the
  // sender, so `signs` is the only honest kind: emailed their own
  // ceremony, same as any co-op agent already added as a guest
  // (Grant, 2026-09-16).
  { key: "buyer_agent_1", label: "Buyer's Agent 1", kind: "stamp", fillRule: owner, tone: "owner", captionWords: ["buyer's agent 1", "buyer's agent", "buyer agent", "agent 1", "agent", "licensee"] },
  { key: "buyer_agent_2", label: "Buyer's Agent 2", kind: "signs", fillRule: manual, tone: "second", optional: true, captionWords: ["buyer's agent 2", "agent 2", "second agent", "licensee 2"] },
  { key: "seller", label: "Seller", kind: "name_only", fillRule: contractCard, tone: "neutral", captionWords: ["seller", "owner"] },
  { key: "listing_agent_1", label: "Listing Agent 1", kind: "name_only", fillRule: party("listing_agent"), tone: "neutral", captionWords: ["listing agent 1", "listing agent", "seller's agent"] },
  { key: "listing_agent_2", label: "Listing Agent 2", kind: "name_only", fillRule: manual, tone: "neutral", optional: true, captionWords: ["listing agent 2"] },
  { key: "escrow", label: "Escrow Officer", kind: "name_only", fillRule: party("escrow"), tone: "neutral", captionWords: ["escrow officer", "escrow", "title company", "title"] },
  { key: "lender", label: "Lender", kind: "name_only", fillRule: party("lender"), tone: "neutral", optional: true, captionWords: ["lender", "loan officer"] },
  // The managing broker really signs (Nate + Grant, 2026-10-26: Mark is a
  // party to every transaction, e.g. the Commission Disbursement
  // Authorization). Deliberately a SEPARATE seat from `broker` below: that
  // one stamps, is never emailed, and the 34 broker-initial boxes on the
  // contract forms rely on it staying that way.
  { key: "managing_broker", label: "Managing Broker", kind: "signs", fillRule: firmRole("managing_broker"), tone: "broker", optional: true, captionWords: ["managing broker"] },
  { key: "broker", label: "Broker", kind: "stamp", fillRule: manual, tone: "broker", optional: true, captionWords: ["designated broker"] },
];

const REALTY_SELL: readonly SigningRole[] = [
  // "Owner" on a listing form is always Seller 1 / Seller 2 (Nate,
  // 2026-09-09); the alias table below makes sure the raw word never
  // surfaces as a role of its own.
  { key: "seller_1", label: "Seller 1", kind: "signs", fillRule: person(0), tone: "first", captionWords: ["seller 1", "first seller", "seller", "owner 1", "owner"] },
  { key: "seller_2", label: "Seller 2", kind: "signs", fillRule: person(1), tone: "second", optional: true, captionWords: ["seller 2", "second seller", "owner 2", "co-owner"] },
  // Same as Buyers 3 and 4 above, for a home owned across two households.
  { key: "seller_3", label: "Seller 3", kind: "signs", fillRule: person(2), tone: "third", optional: true, captionWords: ["seller 3", "third seller", "owner 3"] },
  { key: "seller_4", label: "Seller 4", kind: "signs", fillRule: person(3), tone: "fourth", optional: true, captionWords: ["seller 4", "fourth seller", "owner 4"] },
  { key: "listing_agent_1", label: "Listing Agent 1", kind: "stamp", fillRule: owner, tone: "owner", captionWords: ["listing agent 1", "listing agent", "agent 1", "agent", "licensee"] },
  // Same reasoning as buyer_agent_2 above: a co-listing agent is a
  // colleague, not the sender, so they are emailed their own ceremony
  // rather than stamped with someone else's hand (Grant, 2026-09-16).
  { key: "listing_agent_2", label: "Listing Agent 2", kind: "signs", fillRule: manual, tone: "second", optional: true, captionWords: ["listing agent 2", "agent 2", "second agent", "licensee 2"] },
  { key: "buyer", label: "Buyer", kind: "name_only", fillRule: contractCard, tone: "neutral", captionWords: ["buyer"] },
  { key: "buyer_agent_1", label: "Buyer's Agent 1", kind: "name_only", fillRule: party("buyer_agent"), tone: "neutral", captionWords: ["buyer's agent 1", "buyer's agent", "buyer agent"] },
  { key: "buyer_agent_2", label: "Buyer's Agent 2", kind: "name_only", fillRule: manual, tone: "neutral", optional: true, captionWords: ["buyer's agent 2"] },
  { key: "escrow", label: "Escrow Officer", kind: "name_only", fillRule: party("escrow"), tone: "neutral", captionWords: ["escrow officer", "escrow"] },
  { key: "title", label: "Title", kind: "name_only", fillRule: party("title"), tone: "neutral", optional: true, captionWords: ["title company", "title"] },
  // The managing broker really signs (Nate + Grant, 2026-10-26: Mark is a
  // party to every transaction, e.g. the Commission Disbursement
  // Authorization). Deliberately a SEPARATE seat from `broker` below: that
  // one stamps, is never emailed, and the 34 broker-initial boxes on the
  // contract forms rely on it staying that way.
  { key: "managing_broker", label: "Managing Broker", kind: "signs", fillRule: firmRole("managing_broker"), tone: "broker", optional: true, captionWords: ["managing broker"] },
  { key: "broker", label: "Broker", kind: "stamp", fillRule: manual, tone: "broker", optional: true, captionWords: ["designated broker"] },
];

const MORTGAGE: readonly SigningRole[] = [
  { key: "borrower", label: "Borrower", kind: "signs", fillRule: person(0), tone: "first", captionWords: ["borrower 1", "borrower", "applicant"] },
  { key: "co_borrower", label: "Co-borrower", kind: "signs", fillRule: person(1), tone: "second", optional: true, captionWords: ["co-borrower", "coborrower", "co borrower", "borrower 2", "co-applicant"] },
  { key: "loan_officer_1", label: "Loan Officer", kind: "stamp", fillRule: owner, tone: "owner", captionWords: ["loan officer", "loan originator", "originator", "mlo"] },
  { key: "loan_officer_2", label: "Second Loan Officer", kind: "stamp", fillRule: manual, tone: "owner", optional: true, captionWords: ["loan officer 2", "second loan officer"] },
  { key: "guarantor", label: "Guarantor", kind: "signs", fillRule: manual, tone: "second", optional: true, captionWords: ["guarantor"] },
  { key: "investor", label: "Private Investor", kind: "signs", fillRule: manual, tone: "neutral", optional: true, captionWords: ["investor", "private investor", "lender"] },
];

const INSURANCE: readonly SigningRole[] = [
  { key: "named_insured", label: "Named Insured", kind: "signs", fillRule: person(0), tone: "first", captionWords: ["named insured", "insured", "applicant", "policyholder"] },
  { key: "additional_insured", label: "Additional Insured", kind: "signs", fillRule: person(1), tone: "second", optional: true, captionWords: ["additional insured", "second insured", "spouse"] },
  { key: "agent_1", label: "Agent", kind: "stamp", fillRule: owner, tone: "owner", captionWords: ["agent", "producer", "licensee"] },
];

const PLANNING: readonly SigningRole[] = [
  { key: "grantor_1", label: "Grantor / Applicant", kind: "signs", fillRule: person(0), tone: "first", captionWords: ["grantor", "settlor", "trustor", "applicant", "principal", "testator", "client"] },
  { key: "spouse", label: "Spouse", kind: "signs", fillRule: person(1), tone: "second", optional: true, captionWords: ["spouse", "co-grantor", "co-trustor", "second applicant"] },
  { key: "trustee", label: "Trustee", kind: "signs", fillRule: manual, tone: "neutral", optional: true, captionWords: ["trustee"] },
  { key: "successor_trustee", label: "Successor Trustee", kind: "signs", fillRule: manual, tone: "neutral", optional: true, captionWords: ["successor trustee"] },
  { key: "witness_1", label: "Witness 1", kind: "signs", fillRule: manual, tone: "neutral", optional: true, captionWords: ["witness 1", "first witness", "witness"] },
  { key: "witness_2", label: "Witness 2", kind: "signs", fillRule: manual, tone: "neutral", optional: true, captionWords: ["witness 2", "second witness"] },
  { key: "advisor_1", label: "Advisor", kind: "stamp", fillRule: owner, tone: "owner", captionWords: ["advisor", "agent", "producer"] },
];

const FINANCIAL: readonly SigningRole[] = [
  { key: "account_owner", label: "Account Owner", kind: "signs", fillRule: person(0), tone: "first", captionWords: ["account owner", "primary account holder", "account holder", "client"] },
  { key: "joint_owner", label: "Joint Owner", kind: "signs", fillRule: person(1), tone: "second", optional: true, captionWords: ["joint owner", "joint account holder", "secondary account holder", "co-owner"] },
  { key: "advisor_1", label: "Advisor", kind: "stamp", fillRule: owner, tone: "owner", captionWords: ["advisor", "investment advisor representative", "iar", "representative"] },
];

// Stewardship Productions: team onboarding and contractor paperwork
// (Grant, 2026-09-28). The person signing is a team member or someone
// about to be one, never a client, so the seat fills from the "team
// member" the send screen was opened for (a Parties-style row the
// E-Sign page supplies; see PrepareAndSend's personnelSigner). The
// company's own signature is the sender's stamp, optional because most
// acknowledgments carry only the team member's.
const PRODUCTIONS: readonly SigningRole[] = [
  { key: "team_member", label: "Team Member", kind: "signs", fillRule: party("team_member"), tone: "first", captionWords: ["team member", "employee", "independent contractor", "contractor", "associate", "new hire", "recipient", "acknowledged by"] },
  { key: "productions_signer", label: "Stewardship Productions", kind: "stamp", fillRule: owner, tone: "owner", optional: true, captionWords: ["stewardship productions", "company", "employer", "authorized representative", "authorized signature", "manager", "supervisor"] },
];

/** The catalog, keyed by company slug and side. Realty is the only
 *  company with sides; every other company's list is under `na`. */
const CATALOG: Readonly<Record<string, Partial<Record<RoleSide, readonly SigningRole[]>>>> = {
  realty: { buy: REALTY_BUY, sell: REALTY_SELL, na: REALTY_BUY },
  mortgage: { na: MORTGAGE },
  insurance: { na: INSURANCE },
  planning: { na: PLANNING },
  financial: { na: FINANCIAL },
  productions: { na: PRODUCTIONS },
};

/** A generic seat for a document nobody has taught yet, or a company
 *  the catalog does not know. `signer_1`, `signer_2`, … */
export function genericRole(n: number): SigningRole {
  return {
    key: `signer_${n}`,
    label: `Signer ${n}`,
    kind: "signs",
    fillRule: n <= 2 ? person(n - 1) : manual,
    tone: n === 1 ? "first" : n === 2 ? "second" : "neutral",
    optional: n > 1,
    captionWords: [`signer ${n}`],
  };
}

/** The roles a company offers on one side. Unknown company → the two
 *  generic seats, so a send from a record the catalog does not know
 *  still has somewhere to put a signature. */
export function rolesFor(companySlug: string | null | undefined, side: RoleSide = "na"): readonly SigningRole[] {
  const byCompany = companySlug ? CATALOG[companySlug] : undefined;
  if (!byCompany) return [genericRole(1), genericRole(2)];
  return byCompany[side] ?? byCompany.na ?? [genericRole(1), genericRole(2)];
}

/**
 * The seats a blank FORM is mapped in — which is not the same question
 * as the seats a record is cast into.
 *
 * Casting asks "who is in this deal?", and a Realty deal has one side:
 * an unknown side falls back to the buy-side seats rather than offering
 * a buyer and a seller as the same person. Mapping asks "what blocks
 * does this piece of paper have?", and most AAR forms have BOTH — the
 * Additional Clause Addendum is signed by every buyer and every seller
 * on the same page. Handing such a form one side's seats refused 305 of
 * the atlas's 2,122 reviewed blanks as "not on this form's side", which
 * was the tool being wrong about the paper, not the paper being wrong.
 *
 * AND THE DECLARED SIDE DOES NOT NARROW IT. The Affidavit of Disclosure
 * is a seller's form that the BUYER signs to acknowledge receipt; the
 * Buyer Contingency Addendum is a buyer's form the SELLER signs to
 * accept. Trusting the declared side refused those blocks too (133 of
 * them), because the side says who the form is FOR, not which signature
 * blocks are printed on it. A form therefore offers every seat in the
 * company's catalog, in buy-then-sell order with shared seats appearing
 * once — and it is the SCREEN, not this, that decides which seat a slot
 * starts on.
 */
export function rolesForForm(
  companySlug: string | null | undefined,
  side: RoleSide = "na",
): readonly SigningRole[] {
  const byCompany = companySlug ? CATALOG[companySlug] : undefined;
  if (!byCompany) return rolesFor(companySlug, side);
  const sides = [byCompany.buy, byCompany.sell, byCompany.na].filter(
    (l): l is readonly SigningRole[] => Boolean(l),
  );
  if (sides.length <= 1) return rolesFor(companySlug, side);
  const seen = new Set<string>();
  const out: SigningRole[] = [];
  for (const list of sides) {
    for (const role of list) {
      if (seen.has(role.key)) continue;
      seen.add(role.key);
      out.push(role);
    }
  }
  return out;
}

/** Every role key the catalog knows, across all companies and sides.
 *  Used to validate a role_key arriving on a send. */
export function isKnownRoleKey(key: string): boolean {
  if (/^signer_\d+$/.test(key)) return true;
  for (const byCompany of Object.values(CATALOG)) {
    for (const list of Object.values(byCompany)) {
      if (list?.some((r) => r.key === key)) return true;
    }
  }
  return false;
}

export function isRoleKind(v: unknown): v is RoleKind {
  return v === "signs" || v === "name_only" || v === "stamp";
}

/** `record_person:0`, `record_owner_user`, `party:listing_agent`,
 *  `contract_card:other_party_name`, `manual`. */
export function formatFillRule(rule: FillRule): string {
  switch (rule.kind) {
    case "record_person":
      return `record_person:${rule.index}`;
    case "record_owner_user":
      return "record_owner_user";
    case "party":
      return `party:${rule.partyRole}`;
    case "contract_card":
      return `contract_card:${rule.field}`;
    case "firm_role":
      return `firm_role:${rule.roleKey}`;
    case "manual":
      return "manual";
  }
}

/** The inverse of formatFillRule. Anything unreadable is `manual`: a
 *  rule the OS cannot run must open the row empty, never guess. */
export function parseFillRule(raw: string | null | undefined): FillRule {
  if (!raw) return manual;
  const s = raw.trim();
  const personMatch = /^record_person:(\d+)$/.exec(s);
  if (personMatch) return person(Number(personMatch[1]));
  if (s === "record_owner_user") return owner;
  const partyMatch = /^party:([a-z_]+)$/.exec(s);
  if (partyMatch) return party(partyMatch[1]!);
  if (s === "contract_card:other_party_name") return contractCard;
  const firmMatch = /^firm_role:([a-z_]+)$/.exec(s);
  if (firmMatch) return firmRole(firmMatch[1]!);
  return manual;
}

/** The sentence the roles panel shows under a filled seat. */
export function fillRulePlainEnglish(rule: FillRule): string {
  switch (rule.kind) {
    case "record_person":
      return rule.index === 0
        ? "the record's first person"
        : rule.index === 1
          ? "the record's second person"
          : `the record's person ${rule.index + 1}`;
    case "record_owner_user":
      return "you, the advisor who owns this record";
    case "party":
      return `the ${partyRoleLabel(rule.partyRole)} on the Parties list`;
    case "contract_card":
      return "the name on the Contract card, printed, not asked to sign";
    case "firm_role":
      return `the firm's ${partyRoleLabel(rule.roleKey).toLowerCase()}`;
    case "manual":
      return "you pick each time";
  }
}

function partyRoleLabel(partyRole: string): string {
  return partyRole.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Which role a form's own caption is naming, if any.
 *
 * "Buyer's Signature" → buyer_1; "Buyer 2" and "second buyer" → buyer_2;
 * "Co-Borrower" → co_borrower and never `borrower`. Longer caption words
 * win over shorter ones, so a caption containing "co-borrower" is not
 * also matched by "borrower". A numbered caption ("Buyer (2)",
 * "Signature 2") prefers the `_2` seat. Null when no role's words appear:
 * a field on an unknown caption goes to the active role, as today.
 */
export function matchCaptionToRole(
  caption: string | null | undefined,
  roles: readonly SigningRole[],
): SigningRole | null {
  if (!caption) return null;
  const c = caption.toLowerCase().replace(/[’']/g, "'").replace(/\s+/g, " ").trim();
  if (!c) return null;
  const numbered = /\((\d)\)|\b(\d)\b/.exec(c);
  const ordinal = numbered ? Number(numbered[1] ?? numbered[2]) : null;
  let best: { role: SigningRole; len: number } | null = null;
  for (const role of roles) {
    for (const word of role.captionWords) {
      if (!c.includes(word)) continue;
      // A bare seat word ("buyer") on a caption numbered (2) means the
      // second seat, when the catalog has one.
      const wantsSecond = ordinal === 2 && !/2/.test(word);
      if (wantsSecond) {
        const second = roles.find((r) => r.tone === "second" && r.captionWords.some((w) => c.includes(w.replace(/ 2$/, "")) || w.includes(word)));
        if (second && (!best || word.length > best.len)) best = { role: second, len: word.length + 1 };
        continue;
      }
      // "(3)" / "(4)" on a record-person seat's bare word means that
      // person's seat — Buyer 3, Seller 4 (2026-09-23). Only the
      // parenthesised form, so a stray digit ("page 3") never moves a
      // field; only when the catalog has that seat, else the word keeps
      // its ordinary match below.
      const parenOrdinal = /\((\d)\)/.exec(c);
      const nth = parenOrdinal ? Number(parenOrdinal[1]) : null;
      // Same family only (buyer_1 → buyer_3, never seller_3): a form
      // offers both sides' seats, and index alone would cross them.
      const family = /_\d+$/.test(role.key) ? role.key.replace(/_\d+$/, "") : null;
      if (nth !== null && nth >= 3 && family && !/\d/.test(word) && role.fillRule.kind === "record_person") {
        const seat = roles.find((r) => r.key === `${family}_${nth}`);
        if (seat) {
          // +1 so the numbered seat beats a same-length bare match (the
          // buy side's name-only "Seller" also reads "seller").
          if (!best || word.length + 1 > best.len) best = { role: seat, len: word.length + 1 };
          continue;
        }
      }
      if (!best || word.length > best.len) best = { role, len: word.length };
    }
  }
  return best?.role ?? null;
}

/** The record's second person's seat, for the swap control and for
 *  "Nobody · one person on this record". */
export function secondSeatOf(roles: readonly SigningRole[]): SigningRole | null {
  return roles.find((r) => r.fillRule.kind === "record_person" && r.fillRule.index === 1) ?? null;
}

/**
 * Whether a seat belongs on this send at all. A record-person seat past
 * the second (Buyer 3, Buyer 4) only exists when the record has that
 * many people: every other send would otherwise carry two empty rows it
 * never needs. Seats one and two always show, empty or not — "one
 * person on this record" on Buyer 2 is information a single-buyer send
 * has always shown.
 */
export function seatAppliesToRecord(role: SigningRole, peopleOnRecord: number): boolean {
  if (role.fillRule.kind !== "record_person") return true;
  if (role.fillRule.index < 2) return true;
  return peopleOnRecord > role.fillRule.index;
}

export function firstSeatOf(roles: readonly SigningRole[]): SigningRole | null {
  return roles.find((r) => r.fillRule.kind === "record_person" && r.fillRule.index === 0) ?? null;
}
